// Э-9. История: карточка этажа, отчёт по циклам (план/факт, причины), отчёты смен, версии плана, журнал изменений.

import { useState } from "react";
import { addDays, fmtDate, fmtDateTime, fmtNum, localISODate } from "../../shared/format";
import { setQuery, useRoute } from "../../shared/router";
import { AuditTable, type AuditRow } from "../../shared/audit";
import { TopBar } from "../../shared/shell";
import { Button, Empty, Loading, Select, Tabs, useAsync, useToast } from "../../shared/ui";
import { TaskLine } from "../components";
import { api, can, floorName, floorShort, useCtx } from "../ctx";

type Tab = "cycles" | "floor" | "reports" | "versions" | "audit";

export function HistoryPage({ id }: { id: string }) {
  const { me } = useCtx();
  const r = useRoute();
  const tab = (r.query.get("tab") as Tab) || "cycles";
  const b = useAsync<any>(() => api.get(`/buildings/${id}`), [id]);
  const manager = can(me, "audit");
  const exporter = can(me, "export");
  const items: { value: Tab; label: string }[] = [
    { value: "cycles", label: "Циклы" }, { value: "floor", label: "Карточка этажа" }, { value: "reports", label: "Отчёты смен" },
    { value: "versions", label: "Версии плана" },
  ];
  if (manager) items.push({ value: "audit", label: "Журнал изменений" });
  return (
    <>
      <TopBar title="История" />
      <div className="content">
        <Tabs<Tab> value={tab} onChange={(v) => setQuery({ tab: v === "cycles" ? null : v })} items={items} />
        {tab === "cycles" && <CyclesTab id={id} manager={exporter} />}
        {tab === "floor" && <FloorTab id={id} current={b.data?.current_floor} />}
        {tab === "reports" && <ReportsTab id={id} />}
        {tab === "versions" && <VersionsTab id={id} />}
        {tab === "audit" && manager && <AuditTab id={id} />}
      </div>
    </>
  );
}

function CyclesTab({ id, manager }: { id: string; manager: boolean }) {
  const toast = useToast();
  const d = useAsync<any>(() => api.get(`/buildings/${id}/cycles-report`), [id]);
  if (!d.data) return <Loading error={d.error} onRetry={d.reload} />;
  const done = d.data.rows.filter((x: any) => x.actual_days);
  const avg = done.length ? done.reduce((s: number, x: any) => s + x.actual_days, 0) / done.length : null;
  return (
    <div className="panel">
      <div className="ph">
        <h2>План и факт по этажам{avg ? <span className="muted small"> · средний цикл {fmtNum(avg)} дн.</span> : null}</h2>
        {manager && <Button size="small" onClick={() => api.open(`/buildings/${id}/cycles-report/export.xlsx`, undefined, "cycles.xlsx").catch(toast.error)}>Выгрузить в Excel</Button>}
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th className="num">Этаж</th><th>Начало</th><th>Окончание</th><th className="num">Цикл по плану, дн.</th><th className="num">Факт, дн.</th><th className="num" title="Факт минус план (для идущего этажа — прогноз)">Дольше плана</th><th className="num" title="Насколько окончание этажа позже плановой даты — с учётом задержек нижних этажей">Окончание позже</th><th className="num">Простой, ч</th><th>Основные причины</th></tr></thead>
          <tbody>{d.data.rows.map((x: any) => (
            <tr key={x.cycle} className="click" onClick={() => setQuery({ tab: "floor", floor: String(x.floor) })}>
              <td className="num"><b>{floorShort(x.floor)}</b></td>
              <td>{fmtDate(x.actual_start || x.planned_start)}{!x.actual_start && <span className="muted"> план</span>}</td>
              <td>{x.actual_end ? fmtDate(x.actual_end) : x.status === "not_started" ? "—" : <span className="muted">прогноз {fmtDate(x.forecast_end)}</span>}</td>
              <td className="num">{x.target_days}</td>
              <td className="num">{x.actual_days ?? (x.forecast_days ? <span className="muted">прогноз {x.forecast_days}</span> : "—")}</td>
              <td className="num" style={x.duration_diff > 0 ? { color: "var(--st-loading)", fontWeight: 600 } : undefined}>{x.duration_diff == null ? "—" : x.duration_diff > 0 ? `+${x.duration_diff}` : x.duration_diff}</td>
              <td className="num" style={x.deviation_days > 0 ? { color: "var(--st-loading)", fontWeight: 600 } : undefined}>{x.deviation_days > 0 ? `+${x.deviation_days}` : x.status === "not_started" ? "—" : x.deviation_days}</td>
              <td className="num">{x.downtime_hours ? fmtNum(x.downtime_hours, 0) : "—"}</td>
              <td>{x.main_reasons.join(", ") || <span className="muted">—</span>}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function FloorTab({ id, current }: { id: string; current?: number }) {
  const r = useRoute();
  // этажи корпуса как есть: подземные −1, −2… и добавленные сверху
  const fl = useAsync<{ number: number }[]>(() => api.get(`/buildings/${id}/floors`), [id]);
  const floor = Number(r.query.get("floor") || current || fl.data?.[0]?.number || 1);
  const d = useAsync<any>(() => api.get(`/buildings/${id}/floors/${floor}/history`), [id, floor]);
  const photos = useAsync<any[]>(() => api.get("/photos", { building: id, floor }), [id, floor]);
  return (
    <>
      <Select aria-label="Этаж" style={{ width: 160 }} value={String(floor)} onChange={(e) => setQuery({ floor: e.target.value })}
        options={(fl.data || [{ number: floor }]).map((f) => ({ value: String(f.number), label: floorName(f.number) }))} />
      {!d.data ? <Loading error={d.error} onRetry={d.reload} /> : (
        <div className="split" style={{ display: "grid", gridTemplateColumns: "minmax(0,2fr) minmax(0,1fr)", gap: 16 }}>
          <div className="panel pad stack tight">
            <div className="row between">
              <h2>{floorName(d.data.floor)}</h2>
              <span className="small">план {fmtDate(d.data.planned_start)}–{fmtDate(d.data.planned_end)}{d.data.actual_start ? ` · факт ${fmtDate(d.data.actual_start)}–${d.data.actual_end ? fmtDate(d.data.actual_end) : "…"}` : ""}{d.data.actual_days ? ` · ${d.data.actual_days} дн.` : ""}</span>
            </div>
            {d.data.tasks.map((t: any) => <TaskLine key={t.id} t={t} />)}
          </div>
          <div className="panel pad stack tight">
            <h3>Фото</h3>
            {!photos.data ? <Loading /> : photos.data.length === 0 ? <p className="muted small">Фото с этого этажа нет.</p> : (
              <div className="photos">{photos.data.map((p) => (
                <a key={p.id} href={api.base + `/photos/${p.id}/file?token=${api.token}`} target="_blank" rel="noreferrer" title={p.caption || ""}>
                  <img src={api.base + `/photos/${p.id}/file?thumb=true&token=${api.token}`} alt={p.caption || "фото"} loading="lazy" />
                </a>
              ))}</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function ReportsTab({ id }: { id: string }) {
  const [from, setFrom] = useState(addDays(localISODate(), -7));
  const d = useAsync<any[]>(() => api.get("/reports", { building: id, date_from: from }), [id, from]);
  return (
    <>
      <Select aria-label="Период" style={{ width: 200 }} value={from} onChange={(e) => setFrom(e.target.value)} options={[
        { value: addDays(localISODate(), -7), label: "Последние 7 дней" }, { value: addDays(localISODate(), -30), label: "Последние 30 дней" },
        { value: addDays(localISODate(), -365), label: "За год" },
      ]} />
      {!d.data ? <Loading error={d.error} onRetry={d.reload} /> : d.data.length === 0 ? <Empty>Отчётов за период нет.</Empty> : (
        <div className="panel table-wrap">
          <table className="table">
            <thead><tr><th>Смена</th><th>Работы</th><th>Бригадир</th><th>Люди</th><th>Простой</th><th>Погода</th><th>Статус</th></tr></thead>
            <tbody>{d.data.map((x) => (
              <tr key={x.id}>
                <td>{fmtDate(x.date)}, {x.shift === "day" ? "день" : "ночь"}</td>
                <td className="small">{x.progress.filter((p: any) => !p.superseded).map((p: any) => `${p.grip} ${p.work} ${fmtNum(p.percent, 0)}%`).join("; ") || "—"}
                  {x.progress.some((p: any) => p.correction_of) && <div className="muted">есть исправления руководителя</div>}</td>
                <td>{x.brigadier || "—"}</td>
                <td>{Object.entries(x.headcount || {}).map(([k, v]) => `${k} ${v}`).join(", ") || "—"}</td>
                <td>{x.downtime_hours ? `${fmtNum(x.downtime_hours)} ч, ${x.downtime_reason || ""}` : "—"}</td>
                <td>{[x.weather, x.temperature_c != null ? `${x.temperature_c > 0 ? "+" : ""}${x.temperature_c}°` : null].filter(Boolean).join(", ") || "—"}</td>
                <td><span className={`tag ${x.status === "submitted" ? "s-done" : "s-planned"}`}>{x.status === "submitted" ? "отправлен" : "черновик"}</span></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </>
  );
}

function VersionsTab({ id }: { id: string }) {
  const d = useAsync<any[]>(() => api.get(`/buildings/${id}/plan-versions`), [id]);
  const [open, setOpen] = useState<number | null>(null);
  if (!d.data) return <Loading error={d.error} onRetry={d.reload} />;
  const list = d.data.slice().reverse();
  return (
    <div className="stack tight">
      {list.map((v, n) => {
        const prev = list[n + 1];
        const moved = prev ? v.cycles.filter((c: any) => { const p = prev.cycles.find((x: any) => x.number === c.number); return p && p.end !== c.end; }).length : 0;
        return (
          <div key={v.number} className="panel pad stack tight">
            <div className="row between">
              <b>Версия {v.number}: {v.reason}</b>
              <span className="small muted">{v.user}, {fmtDateTime(v.created_at)}</span>
            </div>
            <div className="small">{prev ? (moved ? `Сдвинуто этажей: ${moved}` : "Даты этажей не изменились") : "Исходный план"} · окончание дома {fmtDate(v.cycles[v.cycles.length - 1]?.end)}</div>
            <Button size="small" variant="ghost" onClick={() => setOpen(open === v.number ? null : v.number)}>{open === v.number ? "Скрыть даты" : "Даты по этажам"}</Button>
            {open === v.number && (
              <div className="small" style={{ columns: "180px" }}>{v.cycles.map((c: any) => <div key={c.number}>{c.floor != null ? floorName(c.floor) : `Цикл ${c.number}`}: {fmtDate(c.start)}–{fmtDate(c.end)}</div>)}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function AuditTab({ id }: { id: string }) {
  const d = useAsync<AuditRow[]>(() => api.get("/audit", { building: id, limit: 2000 }), [id]);
  if (!d.data) return <Loading error={d.error} onRetry={d.reload} />;
  if (d.data.length === 0) return <Empty>Изменений пока нет. Здесь появятся правки плана, отчётов и проблем с указанием автора и причины.</Empty>;
  return <div className="panel"><AuditTable rows={d.data} /></div>;
}
